// The test catalog. Admin only, Pattern B.
//
// What we sell and its default price. The default is a fallback only: a
// customer row always wins, which is why editing a default here never touches
// an existing account's pricing.

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { listTestTypes, updateTestType, removeTestType } from '../api.js'
import { categoryLabel, specimenLabel, formatPrice } from '../vocab.js'
import {
  Button,
  ButtonLink,
  EmptyState,
  Notice,
  Panel,
  Pill,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import { useConfirm } from '../confirm.jsx'

export default function Tests() {
  const confirm = useConfirm()
  const [testTypes, setTestTypes] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [actionError, setActionError] = useState(null)

  const load = useCallback(async () => {
    try {
      const data = await listTestTypes()
      setTestTypes(data.testTypes)
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function toggleActive(testType) {
    setActionError(null)
    try {
      await updateTestType(testType.id, { isActive: !testType.isActive })
      load()
    } catch (caught) {
      setActionError(caught.message)
    }
  }

  async function drop(testType) {
    if (!(await confirm(`Remove ${testType.name}?`))) return
    setActionError(null)
    try {
      await removeTestType(testType.id)
      load()
    } catch (caught) {
      setActionError(caught.message)
    }
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }

  return (
    <>
      <div className="toolbar">
        <span className="spacer" />
        <ButtonLink variant="primary" to="/tests/new">
          Add test type
        </ButtonLink>
      </div>

      {actionError ? <Notice tone="bad">{actionError}</Notice> : null}

      <Panel
        title="Catalog"
        support={testTypes ? `${testTypes.length} test types` : 'Loading'}
        flush
      >
        {testTypes && testTypes.length === 0 ? (
          <EmptyState title="No test types yet">
            Add what we sell. Per customer pricing hangs off each type.
          </EmptyState>
        ) : null}

        {testTypes && testTypes.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>Code</Th>
                <Th>Test</Th>
                <Th>Category</Th>
                <Th>Specimen</Th>
                <Th numeric>Default price</Th>
                <Th>Lab cost</Th>
                <Th>Status</Th>
                <Th aria-label="Actions" />
              </Tr>
            </Thead>
            <Tbody>
              {testTypes.map((testType) => (
                <Tr key={testType.id}>
                  <Td className="tabular">{testType.code}</Td>
                  <Td>
                    <Link to={`/tests/${testType.id}/edit`}>{testType.name}</Link>
                  </Td>
                  <Td>
                    {categoryLabel(testType.category)}
                    {testType.panelSize ? (
                      <div className="ui-cell-meta">{testType.panelSize} panel</div>
                    ) : null}
                  </Td>
                  <Td>{specimenLabel(testType.specimenType)}</Td>
                  <Td numeric={Boolean(testType.defaultUnitPrice)}>
                    {formatPrice(testType.defaultUnitPrice) ?? (
                      <span className="muted">Not set</span>
                    )}
                    {testType.hourlyRate ? (
                      <div className="ui-cell-meta">
                        {`${Number(testType.hourlyMinimum)} hr min at ${formatPrice(testType.hourlyRate)}/hr`}
                      </div>
                    ) : null}
                  </Td>
                  <Td>{testType.hasLabCogs ? 'Yes' : 'No'}</Td>
                  <Td>
                    <Pill tone={testType.isActive ? 'ok' : 'neutral'}>
                      {testType.isActive ? 'Active' : 'Inactive'}
                    </Pill>
                  </Td>
                  <Td numeric>
                    <Button variant="ghost" onClick={() => toggleActive(testType)}>
                      {testType.isActive ? 'Deactivate' : 'Activate'}
                    </Button>{' '}
                    <Button variant="ghost" onClick={() => drop(testType)}>
                      Remove
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : null}
      </Panel>
    </>
  )
}
